/**
 * Content Element Reorder — Constants & Utilities
 * 
 * Phase 3 of Layout Nudge system.
 * Controls the render order of elements within SlideContent.
 * 
 * Header (kicker + title) ALWAYS renders first.
 * Footer (CTA) ALWAYS renders last.
 * Everything in between is reorderable via slide.contentOrder.
 */

/**
 * Default content order — matches the original hardcoded order in SlideContent.
 * When slide.contentOrder is undefined/null, this order is used.
 */
export const DEFAULT_CONTENT_ORDER = [
  'subtitle',
  'body',
  'imageWidget',
  'videoWidget',
  'callout',
  'progress',
  'stats',
  'paragraphs',
  'bullets',
  'tags',
];

/**
 * Human-readable labels for each content element
 */
export const CONTENT_ELEMENT_LABELS = {
  subtitle: 'Subtitle',
  body: 'Body Text',
  imageWidget: 'Image',
  videoWidget: 'Video',
  callout: 'Callout',
  progress: 'Progress Bar',
  stats: 'Stats Grid',
  paragraphs: 'Paragraphs',
  bullets: 'Bullet Points',
  tags: 'Tags',
};

/**
 * Group classification for each element
 */
export const CONTENT_ELEMENT_GROUPS = {
  subtitle: 'text',
  body: 'text',
  paragraphs: 'text',
  imageWidget: 'media',
  videoWidget: 'media',
  callout: 'media',
  progress: 'data',
  stats: 'data',
  bullets: 'data',
  tags: 'data',
};

/**
 * Group colors for visual distinction in the reorder UI
 */
export const GROUP_COLORS = {
  text: '#34d399',    // emerald — matches scope LED green
  media: '#a78bfa',   // violet
  data: '#f59e0b',    // amber
};

/**
 * Get the effective content order for a slide.
 * Returns DEFAULT_CONTENT_ORDER if no custom order is set.
 * Ensures all elements are present (handles future additions gracefully).
 * 
 * @param {string[]|undefined|null} contentOrder - The slide's contentOrder array
 * @returns {string[]} The resolved content order
 */
export function getEffectiveContentOrder(contentOrder) {
  if (!contentOrder || !Array.isArray(contentOrder) || contentOrder.length === 0) {
    return DEFAULT_CONTENT_ORDER;
  }

  // Ensure all known elements are present (add missing ones at the end)
  const result = [...contentOrder];
  for (const element of DEFAULT_CONTENT_ORDER) {
    if (!result.includes(element)) {
      result.push(element);
    }
  }

  // Remove any unknown elements (from deleted features, etc.)
  return result.filter(el => DEFAULT_CONTENT_ORDER.includes(el));
}

/**
 * Check if a content element is "active" (has content or is enabled).
 * Used to show only relevant elements in the reorder UI.
 * 
 * @param {string} elementId - The element identifier
 * @param {Object} data - The slide data
 * @returns {boolean} Whether the element is active/visible
 */
export function isElementActive(elementId, data) {
  switch (elementId) {
    case 'subtitle':
      return data.subtitleEnabled !== false && !!data.subtitle;
    case 'body':
      return data.bodyEnabled !== false && !!data.body;
    case 'imageWidget':
      return !!data.imageWidget;
    case 'videoWidget':
      return !!data.videoWidget;
    case 'callout':
      return !!data.callout;
    case 'progress':
      return !!data.progress;
    case 'stats':
      return !!(data.stats && data.stats.length > 0);
    case 'paragraphs':
      return !!(data.paragraphs && data.paragraphs.length > 0);
    case 'bullets':
      return data.bulletsEnabled !== false && !!(data.bullets && data.bullets.length > 0);
    case 'tags':
      return data.tagsEnabled !== false && !!(data.meta && data.meta.length > 0);
    default:
      return false;
  }
}
